/**
 * Age of Steam - Puerto Rico Solo Play ゲーム定義（Boardgame.io）
 *
 * Puerto Rico 固有ルールに従ったソロプレイ専用ゲーム実装。
 *
 * 実装済み Phase:
 * - Phase 1: 株式発行（制限なし）
 * - Phase 3: アクション選択（2種類のみ、$5コスト、任意）
 * - Phase 4-8: 通常ルール（buildTrack, moveGoods, collectIncome, payExpenses, incomeReduction）
 * - Phase 10: ターン進行
 *
 * スキップされる Phase:
 * - Phase 2: プレイ順決定（ソロプレイのため）
 * - Phase 9: 商品補充（Puerto Rico では不使用）
 *
 * @see docs/specifications/logic/puerto_rico/*.md
 * @see data/map/puerto_rico/puerto_rico_rules.md
 */

import { Game } from 'boardgame.io';
import type { PuertoRicoGameState, PuertoRicoDifficulty } from '../types';
import { setupPuertoRicoGame } from './setupPuertoRico';
import { wrapPhaseMoves } from './utils/moveLogger';
import { issueSharesPhase } from './phases/issueSharesPhase';
import { selectActionsPuertoRicoPhase } from './phases/selectActionsPuertoRicoPhase';
import { buildTrackPhase } from './phases/buildTrackPhase';
import { moveGoodsPhase } from './phases/moveGoodsPhase';
import { collectIncomePhase } from './phases/collectIncomePhase';
import { payExpensesPhase } from './phases/payExpensesPhase';
import { incomeReductionPuertoRicoPhase } from './phases/incomeReductionPuertoRicoPhase';
import { advanceTurnPuertoRicoPhase } from './phases/advanceTurnPuertoRicoPhase';
import { endGamePuertoRico } from './utils/gameEndPuertoRico';

/**
 * Puerto Rico ゲームのセットアップオプション
 */
export interface PuertoRicoSetupData {
  difficulty?: PuertoRicoDifficulty;
}

/**
 * Age of Steam - Puerto Rico Solo Play ゲーム定義
 */
export const PuertoRicoGame: Game<PuertoRicoGameState> = {
  name: 'age-of-steam-puerto-rico',

  // デフォルトのターン動作
  turn: {},

  setup: ({ ctx }: { ctx: any }, setupData?: PuertoRicoSetupData) => {
    // Boardgame.io v0.50では、setup関数の第1引数は { ctx, ... } の形式
    // setupData が渡されない場合は undefined を渡し、setupPuertoRicoGame 内で localStorage から読み込む
    return setupPuertoRicoGame(ctx, setupData?.difficulty);
  },

  // プレイヤー人数の範囲（Puerto Rico Solo Play: 固定1人）
  minPlayers: 1,
  maxPlayers: 1,

  phases: wrapPhaseMoves({
    // Phase 1: 株式発行（共通Phase）
    issueShares: issueSharesPhase as any,

    // Phase 2: プレイ順決定はPuerto Ricoではスキップ

    // Phase 3: アクション選択（Puerto Rico固有: 2種類、$5コスト、任意）
    selectActions: selectActionsPuertoRicoPhase,

    // Phase 4: 線路建設（共通Phase）
    buildTrack: buildTrackPhase as any,

    // Phase 5: 商品輸送（共通Phase）
    moveGoods: moveGoodsPhase as any,

    // Phase 6: 収入獲得（自動処理Phase）
    collectIncome: collectIncomePhase as any,

    // Phase 7: 経費支払い（自動処理Phase）
    payExpenses: payExpensesPhase as any,

    // Phase 8: 収入減少（自動処理Phase、Puerto Rico専用: Phase 9スキップ）
    incomeReduction: incomeReductionPuertoRicoPhase as any,

    // Phase 9: 商品補充（Puerto Rico ではスキップ）
    // refillGoods は不要

    // Phase 10: ターン進行（自動処理Phase + ゲーム終了判定）
    advanceTurn: advanceTurnPuertoRicoPhase as any
  }),

  // ゲーム終了条件
  endIf: ({ G }: { G: PuertoRicoGameState }) => {
    if (G.isGameOver) {
      // Puerto Rico Solo Play: 勝敗はisVictoriousで判定
      const soloPlayer = G.players['0'];
      return {
        winner: soloPlayer.isVictorious ? '0' : undefined
      };
    }
  },

  // ゲーム終了時の処理
  onEnd: ({ G }: { G: PuertoRicoGameState }) => {
    // ゲーム終了処理を実行（スコア計算など）
    if (!G.isGameOver) {
      endGamePuertoRico(G);
    }
  }
};
